/**
 * Spend vs. revenue over time (A7Y-2054).
 *
 * recharts ComposedChart: grouped bars for spend + revenue with a net line
 * overlaid. `monthly` arrives NEWEST-FIRST from the API; we sort ascending so
 * the x-axis reads left→right chronologically.
 */
import { formatCurrency } from "@/lib/utils";
import type { PnlMonthSummary } from "@a7y/types";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatMonthLabel, formatMonthShort } from "../../format";

const SPEND_COLOR = "#3b82f6"; // blue-500
const REVENUE_COLOR = "#22c55e"; // green-500
const NET_COLOR = "#8b5cf6"; // violet-500

/** Compact axis label, e.g. 1234 → "$1.2k", 950 → "$950". */
function compactCurrency(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `${sign}$${Math.round(abs)}`;
}

export function SpendVsRevenueChart({ monthly }: { monthly: PnlMonthSummary[] }) {
  const data = [...monthly]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => ({ ...m, label: formatMonthShort(m.month) }));

  const hasData = data.some((d) => d.spend !== 0 || d.revenue !== 0);

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">
        Spend vs. revenue
      </h2>
      {!hasData ? (
        <div className="flex h-[280px] items-center justify-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No spend or revenue recorded yet.
          </p>
        </div>
      ) : (
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e4e4e7"
                className="dark:opacity-20"
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={{ stroke: "#374151", strokeWidth: 0.5 }}
                tickLine={false}
                dy={6}
              />
              <YAxis
                width={52}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={compactCurrency}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#18181b",
                  border: "1px solid #3f3f46",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "#f4f4f5",
                }}
                labelFormatter={(label: string, payload) => {
                  const month = payload?.[0]?.payload?.month as string | undefined;
                  return month ? formatMonthLabel(month) : label;
                }}
                formatter={(value: number, name: string) => [formatCurrency(value), name]}
                cursor={{ fill: "rgba(148,163,184,0.12)" }}
              />
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              />
              <Bar dataKey="spend" name="Spend" fill={SPEND_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="revenue" name="Revenue" fill={REVENUE_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Line
                type="monotone"
                dataKey="net"
                name="Net"
                stroke={NET_COLOR}
                strokeWidth={2}
                dot={{ r: 2.5, strokeWidth: 0, fill: NET_COLOR }}
                activeDot={{ r: 4, strokeWidth: 0, fill: NET_COLOR }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
